import type { PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';
import { GuildActivityFeedPanel } from '@/components/activity/GuildActivityFeedPanel';
import { GuildChatPanel } from '@/components/chat/GuildChatPanel';
import { BrandMark } from '@/components/shared/BrandMark';
import { Button } from '@/components/shared/Button';
import { NoticeBanner } from '@/components/shared/NoticeBanner';
import { RoleEmblem } from '@/components/shared/RoleEmblem';
import { navigationItems } from '@/data/navigation';
import { getPlayerAttendanceStat } from '@/features/attendance/selectors';
import { getAttendanceLevel } from '@/features/progression/logic';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { getProfileInGameName } from '@/utils/profile';
import { formatAppRoleLabel, getPrimaryRole } from '@/utils/roles';

export function AppShell({ children }: PropsWithChildren): JSX.Element {
  const { profile, logout, hasAnyRole } = useAuth();
  const { notice, clearNotice, state } = useGuildData();
  const initials = profile?.displayName?.slice(0, 1).toUpperCase() ?? 'W';
  const profileAttendance = profile ? getPlayerAttendanceStat(state, profile.id) : null;
  const profileAttendanceCount = profileAttendance?.totalCountedMasses ?? 0;
  const profileLevel = profile ? profile.progressionLevelOverride ?? getAttendanceLevel(profileAttendanceCount) : null;
  const profileRole = profile ? getPrimaryRole(profile.roles) : null;

  return (
    <div className="shell">
      <aside className="shell__sidebar">
        <BrandMark />
        <nav className="shell__nav">
          {navigationItems
            .filter((item) => !item.roles || hasAnyRole(item.roles))
            .map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) => (isActive ? 'shell__nav-link shell__nav-link--active' : 'shell__nav-link')}
              >
                {item.label}
              </NavLink>
            ))}
        </nav>

        <div className="shell__sidebar-footer">
          <p className="shell__sidebar-label">Signed in as</p>
          <strong>{profile?.displayName ?? 'Guest'}</strong>
          {profile ? <span>IGN: {getProfileInGameName(profile)}</span> : null}
          {profile && profileRole ? <RoleEmblem role={profileRole} /> : <span>Not authenticated</span>}
          {profile && profileRole ? <span>{formatAppRoleLabel(profileRole)} - Level {profileLevel}</span> : null}
          <a
            className="button button--ghost shell__discord-link"
            href="https://discord.gg/n8w3FP78"
            target="_blank"
            rel="noreferrer"
          >
            Join Guild Discord
          </a>
          <Button variant="secondary" onClick={logout}>
            Sign Out
          </Button>
        </div>
      </aside>

      <div className="shell__content">
        <header className="shell__topbar">
          <div className="shell__topbar-copy">
            <p className="shell__topbar-eyebrow">Woof Woof</p>
            <h2>Guild Operations</h2>
          </div>

          <div className="shell__topbar-profile">
            {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <div className="shell__topbar-avatar">{initials}</div>}
            <div className="shell__topbar-profile-copy">
              <strong>{profile?.displayName ?? 'Guest'}</strong>
              <span>
                {profile && profileRole
                  ? `${getProfileInGameName(profile)} - ${formatAppRoleLabel(profileRole)} - Level ${profileLevel} - ${profile.approvalStatus}`
                  : 'Signed out'}
              </span>
            </div>
            {profile && profileRole ? <RoleEmblem role={profileRole} /> : null}
          </div>
        </header>

        {notice ? <NoticeBanner notice={notice} onDismiss={clearNotice} /> : null}

        <div className="shell__workspace">
          <main className="shell__main">{children}</main>
          <aside className="shell__rail">
            <div className="shell__utility-stack">
              <GuildActivityFeedPanel />
              <GuildChatPanel />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
